import React, {useMemo} from 'react';
import {Select} from '@mantine/core';
import {FormFieldTypeEnum} from 'src/api/ProactApiClient';
import {useGetFormDefinitionFieldListQuery} from 'src/redux/queries/formDefinition';

/**
 * Form field types that support value-based notifications
 */
const SUPPORTED_FIELD_TYPES = [
  FormFieldTypeEnum.Switch,
  FormFieldTypeEnum.Date,
  FormFieldTypeEnum.DateTime,
  FormFieldTypeEnum.DropdownSingle,
  FormFieldTypeEnum.DropdownSingleAutocomplete,
  FormFieldTypeEnum.DropdownMultiModal,
  FormFieldTypeEnum.DropdownMultiPersonnelModal,
  FormFieldTypeEnum.Scale,
  FormFieldTypeEnum.Text,
  FormFieldTypeEnum.Time,
];

export interface FormFieldSelectorProps {
  formDefinitionId: number;
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

/**
 * Async field selector for Form module notification rules.
 * Fetches available form fields from the API and presents them in a dropdown.
 */
export const FormFieldSelector: React.FC<FormFieldSelectorProps> = ({
  formDefinitionId,
  value,
  onChange,
  disabled,
}) => {
  const {data: fieldsPayload, isLoading} = useGetFormDefinitionFieldListQuery({
    start: 0,
    length: 10000,
    formDefinitionId,
    fieldTypes: SUPPORTED_FIELD_TYPES,
  });

  const options = useMemo(() => {
    return (fieldsPayload?.data ?? [])
      .filter(field => !!field.invariantId)
      .map(field => ({
        value: field.invariantId,
        label: field.label,
      }));
  }, [fieldsPayload?.data]);

  const handleChange = (val: string | null) => {
    onChange(val ?? '');
  };

  return (
    <Select
      data-testid="form-field-select"
      data={options}
      value={value ?? null}
      onChange={handleChange}
      disabled={disabled || isLoading}
      placeholder={isLoading ? 'Loading fields...' : 'Select field'}
      searchable
      w={{base: '100%', lg: 200}}
    />
  );
};
